import { zodResolver } from '@hookform/resolvers/zod';
import { Collapse, ExpandMoreRoundedIcon, Flex, Form, Rotate } from '@shared/components';
import { useState } from 'react';
import { type SubmitHandler, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useUserList } from '../../provider/use-user-list';
import { UserStatus } from '../../types';

import { FilterUserForm } from './filter-user-form';
import { UserFilterButton } from './user-filter-button';
import { UserSearchBox } from './user-search-box';

const UserSearchFilterBoxInputsSchema = z.object({
  role: z.object({ label: z.string(), value: z.string() }).optional().nullable(),
  status: z.nativeEnum(UserStatus).or(z.literal('')).optional(),
});

export type UserSearchFilterBoxInputs = z.infer<typeof UserSearchFilterBoxInputsSchema>;

interface UserSearchFilterBoxProps {
  withSearch?: boolean;
  withStatusFilter?: boolean;
  withAppRoleFilter?: boolean;
  withSearchBoxFullWidth?: boolean;
}

export function UserSearchFilterBox(props: Readonly<UserSearchFilterBoxProps>) {
  const {
    withSearch = false,
    withAppRoleFilter = false,
    withStatusFilter = false,
    withSearchBoxFullWidth = false,
  } = props;
  const { searchAndFilterInput, setSearchAndFilterInput, setDataGridParams } = useUserList();
  const [isShowFilterBox, setIsShowFilterBox] = useState(Boolean(searchAndFilterInput.status));
  const formContext = useForm<UserSearchFilterBoxInputs>({
    defaultValues: {
      role: { label: '', value: '' },
      status: searchAndFilterInput.status,
    },
    resolver: zodResolver(UserSearchFilterBoxInputsSchema),
  });

  const onSubmit: SubmitHandler<UserSearchFilterBoxInputs> = (formValues) => {
    const { status, role } = formValues;
    setSearchAndFilterInput({ status, role: role?.value ?? '' });
    setDataGridParams({ page: 0 });
  };

  const toggleFilterBox = () => {
    setIsShowFilterBox((prev) => !prev);
  };

  const onReset = () => {
    formContext.reset({ role: { label: '', value: '' }, status: '' as UserStatus });
  };

  return (
    <FormProvider {...formContext}>
      <Flex sx={{ justifyContent: 'space-between', mb: 6 }}>
        {withSearch ? <UserSearchBox hasFullWidth={withSearchBoxFullWidth} /> : null}
        {withStatusFilter || withAppRoleFilter ? (
          <UserFilterButton
            onClick={toggleFilterBox}
            endIcon={
              <Rotate in={isShowFilterBox}>
                <ExpandMoreRoundedIcon />
              </Rotate>
            }
          />
        ) : null}
      </Flex>
      {withStatusFilter || withAppRoleFilter ? (
        <Form onSubmit={formContext.handleSubmit(onSubmit)} onReset={onReset}>
          <Collapse in={isShowFilterBox} timeout='auto' unmountOnExit>
            <FilterUserForm
              sx={{ mb: 6 }}
              withStatusFilter={withStatusFilter}
              withAppRoleFilter={withAppRoleFilter}
            />
          </Collapse>
        </Form>
      ) : null}
    </FormProvider>
  );
}
